import React, { useState } from 'react';

import dayjs from '~/timeInit';

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DateRangePicker, LocalizationProvider, SingleInputDateRangeField } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';

import { days } from './model';
import { REPORT_FILTER_DEFAULTS, reportFilterChanges, reportFilterDraft, reportFilterProblem } from './reportFilters';

type Bounds = { from: string; to: string; problem?: string };
type Props = {
  params: URLSearchParams;
  currency: string;
  currencies: string[];
  rooms: string[];
  comparison: boolean;
  vendor: boolean;
  cutoff: string;
  currentBounds?: Bounds;
  comparisonBounds?: Bounds;
  onClose: () => void;
  onApply: (draft: Record<string, string>) => void;
};
export default function ReportFiltersDialog({
  params,
  currency,
  currencies,
  rooms,
  comparison,
  vendor,
  cutoff,
  currentBounds,
  comparisonBounds,
  onClose,
  onApply,
}: Props) {
  const [initial] = useState(() => reportFilterDraft(params, currency));
  const [draft, setDraft] = useState(initial);
  const set = (key: string, value: string) => setDraft((previous) => ({ ...previous, [key]: value }));
  const problem = reportFilterProblem(draft, cutoff, comparison);
  const maxDay = Math.max(1, days(draft.from, draft.to) || 0, comparison ? days(draft.cmpFrom, draft.cmpTo) || 0 : 0);
  const select = (label: string, key: string, options: string[]) => (
    <TextField
      key={key}
      select
      size="small"
      fullWidth
      label={label}
      value={draft[key]}
      onChange={(event) => set(key, event.target.value)}
    >
      {options.map((option) => (
        <MenuItem key={option} value={option}>
          {option}
        </MenuItem>
      ))}
    </TextField>
  );
  return (
    <Dialog open fullWidth maxWidth="sm" onClose={onClose} aria-labelledby="report-filters-title">
      <DialogTitle id="report-filters-title">Dates & filters</DialogTitle>
      <DialogContent dividers>
        <Stack gap={2.5} sx={{ overflow: 'visible' }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {(['current', ...(comparison ? ['comparison'] : [])] as const).map((side) => {
              const start = side === 'current' ? 'from' : 'cmpFrom',
                end = side === 'current' ? 'to' : 'cmpTo';
              const bounds = side === 'current' ? currentBounds : comparisonBounds;
              return (
                <Box key={side}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1, overflow: 'visible' }}
                  >
                    <Typography variant="subtitle2">
                      {side === 'current' ? 'Current offer dates' : 'Comparison dates'}
                    </Typography>
                    <Button
                      size="small"
                      disabled={!bounds || !!bounds.problem}
                      onClick={() => setDraft((previous) => ({ ...previous, [start]: bounds.from, [end]: bounds.to }))}
                    >
                      Use offer dates
                    </Button>
                  </Stack>
                  <DateRangePicker
                    slots={{ field: SingleInputDateRangeField }}
                    label={side === 'current' ? 'Current date range' : 'Comparison date range'}
                    value={[draft[start] ? dayjs(draft[start]) : null, draft[end] ? dayjs(draft[end]) : null]}
                    onChange={(value) =>
                      setDraft((previous) => ({
                        ...previous,
                        [start]: value[0]?.format('YYYY-MM-DD') ?? '',
                        [end]: value[1]?.format('YYYY-MM-DD') ?? '',
                      }))
                    }
                    format="DD MMM YYYY"
                    maxDate={dayjs(cutoff)}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </Box>
              );
            })}
          </LocalizationProvider>
          {vendor && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Reporting window
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ overflow: 'visible' }}>
                {['Full Period', 'First 30 Days', 'First 60 Days', 'First 90 Days'].map((period) => (
                  <Chip
                    key={period}
                    label={period}
                    color={draft.period === period ? 'primary' : 'default'}
                    variant={draft.period === period ? 'filled' : 'outlined'}
                    onClick={() => set('period', period)}
                  />
                ))}
              </Stack>
            </Box>
          )}
          <Divider />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {select('Refund basis', 'refund', ['Before Refund', 'After Refund'])}
            {select('Currency', 'currency', currencies)}
            {select('Room type', 'room', ['All', ...rooms])}
            {select('Package type', 'packageType', ['All', 'Base', 'Indulgence'])}
            {vendor &&
              select('Check-in chart bookings', 'booking', [
                'All',
                'BNBL Bookings',
                'Bookings Before Today',
                'Upcoming Bookings Only',
              ])}
          </Box>
          {vendor && (
            <Box sx={{ px: 1 }}>
              <Typography variant="subtitle2">Chart day range</Typography>
              <Typography variant="caption" color="text.secondary">
                Day {draft.axisStart} – {draft.axisDays === '0' ? `${maxDay} (auto)` : draft.axisDays}
              </Typography>
              <Slider
                aria-label="Chart day range"
                value={[
                  Math.min(Number(draft.axisStart) || 0, maxDay),
                  Math.min(Number(draft.axisDays) || maxDay, maxDay),
                ]}
                min={0}
                max={maxDay}
                step={1}
                disableSwap
                valueLabelDisplay="auto"
                getAriaLabel={(index) => (index === 0 ? 'Chart start day' : 'Chart end day')}
                onChange={(_, value) => {
                  const [start, end] = value as number[];
                  setDraft((previous) => ({
                    ...previous,
                    axisStart: String(start),
                    axisDays: end === maxDay ? '0' : String(end),
                  }));
                }}
              />
            </Box>
          )}
          {problem && <Alert severity="error">{problem}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={() => setDraft((previous) => ({ ...previous, ...REPORT_FILTER_DEFAULTS, currency }))}>
          Reset filters
        </Button>
        <Box>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" disabled={!!problem} onClick={() => onApply(reportFilterChanges(initial, draft))}>
            Apply filters
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
